const STORAGE_KEY = 'reportQaPreviewSession';

export interface QaPreviewSession {
  reportId: string;
  /** Project the report is previewed in; null when previewed standalone. */
  projectId: number | null;
  returnPath: string;
}

/**
 * Remember which report is being QA previewed so drill-down pages reached
 * from the preview (product details, flavor/texture charts) can render a
 * report that is not yet part of an approved project, and so browser-back
 * returns to the preview instead of the project summary.
 */
export const setQaPreviewSession = (session: QaPreviewSession): void => {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(session));
  } catch {
    // sessionStorage may be unavailable (e.g. privacy mode); preview still works
  }
};

export const clearQaPreviewSession = (): void => {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
};

/**
 * Matches the previewed report, or any report drilled into from the project
 * the preview is shown in (the report selector lists the project's reports).
 */
export const getQaPreviewSession = (
  reportId: string | undefined,
  projectId?: number | string,
): QaPreviewSession | null => {
  if (!reportId) return null;
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const session = JSON.parse(raw) as Partial<QaPreviewSession>;
    if (typeof session?.returnPath !== 'string') return null;
    const projectMatches =
      typeof session.projectId === 'number' &&
      projectId !== undefined &&
      Number(projectId) === session.projectId;
    return session.reportId === reportId || projectMatches
      ? (session as QaPreviewSession)
      : null;
  } catch {
    return null;
  }
};
